import { isGuest } from '@/helpers'
import useAuthentication from '@/modules/authentication/useAuthentication'
import useError from '@/modules/error/useError'
import { PortalLayoutType, PortalType } from '@/modules/portalSettings/types'
import useBranding from '@/modules/portalSettings/useBranding'
import { trackWebVitals } from '@/modules/tracking/useTracking'
import useVersion from '@/modules/version/useVersion'
import StartPage from '@/pages/StartPage.vue'
import { getGuestQuoteRoutes } from '@/router/additionalRoutes.ts'
import { quotesPortalRoutes } from '@/router/quotesPortalRoutes'
import { getRoutes } from '@/router/routes'
import useRequestManager from '@/store/useRequestManager'
import {
  createRouter,
  createWebHistory,
  NavigationGuardReturn,
  RouteLocationNormalizedGeneric,
  RouteLocationNormalizedLoadedGeneric,
  RouteLocationRaw
} from 'vue-router'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [{ path: '/:pathMatch(.*)*', component: StartPage, name: 'Placeholder' }]
})

const addRoutes = (): void => {
  const routes = getRoutes()
  routes.forEach((route) => router.addRoute(route))
}

const addQuotesPortalRoutes = (): void => {
  quotesPortalRoutes.forEach((route) => router.addRoute(route))
}

const addGuestQuoteRoutes = () => {
  getGuestQuoteRoutes().forEach((route) => router.addRoute(route))
}

const executeInitialRouting = async (
  to: RouteLocationNormalizedGeneric
): Promise<RouteLocationRaw | void> => {
  const portalBranding = await useBranding().getBranding()
  const portalLayoutType = portalBranding.portalLayoutType
  router.removeRoute('Placeholder')
  if (portalLayoutType === PortalLayoutType.DEFAULT_LAYOUT) {
    addRoutes()
    if (portalBranding.hasGuestQuotes) {
      // Essential shop still has guest quote routes
      addGuestQuoteRoutes()
    }
    return { path: to.fullPath, query: { ...to.query }, replace: true }
  } else if (portalLayoutType === PortalLayoutType.PORTAL_UNAVAILABLE_LAYOUT) {
    return
  } else if (portalLayoutType === PortalLayoutType.QUOTES_PORTAL_LAYOUT) {
    addQuotesPortalRoutes()
    addGuestQuoteRoutes()
  }
  return { path: to.fullPath, query: { ...to.query }, replace: true }
}

const NOT_HANDLED = Symbol('notHandled')
type LoginRerouteResult = NavigationGuardReturn | typeof NOT_HANDLED

const handleTokenLogin = async (
  to: RouteLocationNormalizedGeneric,
  token: string,
  redirectPath: string
): Promise<LoginRerouteResult> => {
  if (to.path === '/authToken' || to.path === '/subsidiarylogin') {
    useAuthentication().loginWithAccessToken({ value: token })
    if (to.query.startPageSerial) {
      return redirectPath + `?startPageSerial=${to.query.startPageSerial}`
    } else {
      return redirectPath || '/'
    }
  }

  if (to.path === '/punchoutlogin') {
    await useAuthentication().loginViaPunchout({ value: token })
    return redirectPath || '/'
  }

  if (to.path === '/oauthlogin') {
    await useAuthentication().loginViaOauth({ value: token })
    // Handle popup flow for SAML SSO
    if (globalThis.opener && !globalThis.opener.closed) {
      // Notify the opener window that login was successful
      globalThis.opener.postMessage({ type: 'saml_login_success' }, globalThis.location.origin)
      // Close popup after a small delay to ensure message is sent
      setTimeout(() => globalThis.close(), 100)
      // Abort navigation in the popup
      return false
    }
    // Normal oauth login flow (not popup)
    return redirectPath || '/'
  }

  if (to.path === '/autologin') {
    await useAuthentication().autoLogin({ value: token })
    return '/'
  }

  return NOT_HANDLED
}

const tryLoginRerouting = async (
  to: RouteLocationNormalizedGeneric
): Promise<LoginRerouteResult> => {
  const token = to.query?.token
  const jwt = to.query?.jwt
  const redirectPath = to.query.redirect ? to.query.redirect.toString() : ''

  if (token) {
    return handleTokenLogin(to, token.toString(), redirectPath)
  }

  if (jwt && to.path.startsWith('/guest')) {
    useAuthentication().guestLogin({ value: jwt.toString() })
    return NOT_HANDLED
  }

  if (to.path.startsWith('/login')) {
    const portalBranding = useBranding().branding.value
    if (
      portalBranding?.supplierPortalLoginUrl &&
      portalBranding.portalType === PortalType.SupplierPortal
    ) {
      const redirectParam = globalThis.location.origin + redirectPath
      globalThis.location.href =
        portalBranding.supplierPortalLoginUrl + '?redirect=' + encodeURIComponent(redirectParam)
    }
  }

  return NOT_HANDLED
}

const checkAuthenticationForRoute = (to: RouteLocationNormalizedGeneric): NavigationGuardReturn => {
  const authentication = useAuthentication()
  if (to.matched.some((record) => record.meta.requiresAuth)) {
    if (authentication.hasValidAccessToken()) {
      return true
    } else if (to.path == '/') {
      return { name: 'Login' }
    } else {
      return { name: 'Login', query: { redirect: to.fullPath } }
    }
  } else if (to.path === '/login' && authentication.hasValidAccessToken()) {
    return { name: 'StartPage' }
  } else {
    return true
  }
}

router.beforeEach(
  async (
    to: RouteLocationNormalizedGeneric,
    _from: RouteLocationNormalizedLoadedGeneric
  ): Promise<NavigationGuardReturn> => {
    if (useBranding().branding.value == undefined) {
      return await executeInitialRouting(to)
    }

    if (to.query['redslave']) {
      useAuthentication().setRedslave(true)
    }

    // remove red/cust/# from old links
    if (to.fullPath.includes('/red/cust#')) {
      return to.fullPath.replace('/red/cust#', '')
    }

    // cancel pending requests
    useRequestManager().cancelPendingRequests()

    const loginReroute = await tryLoginRerouting(to)
    if (loginReroute !== NOT_HANDLED) {
      return loginReroute
    }

    // check authentication
    return checkAuthenticationForRoute(to)
  }
)

router.afterEach((to, from) => {
  // scroll to top
  // replaces scrollBehavior because of bug when route has //
  // https://github.com/vuejs/vue-router/issues/2593
  if (globalThis.scrollTo) {
    globalThis.scrollTo(0, 0)
  }

  // clear errors
  useError().reset()

  if (from.name !== to.name && !isGuest.value) {
    trackWebVitals()
  }
})

router.beforeResolve(() => {
  // check app version & hash
  if (import.meta.env.PROD) {
    useVersion().checkVersion()
    if (useVersion().state.value.hashChanged) {
      globalThis.location.reload()
    }
  }
})

export default router
